import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Modal,
  TextInput,
  Linking,
  Dimensions,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { PlanService, WorkoutService, AuthService, CheckInService } from '../../services/ApiService';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const WorkoutPlanScreen = () => {
  const insets = useSafeAreaInsets();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [todayWorkout, setTodayWorkout] = useState(null);
  const [todayLog, setTodayLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showFullPlan, setShowFullPlan] = useState(false);
  const [workoutData, setWorkoutData] = useState({
    exercises: [],
    totalWorkoutTime: '',
    notes: '',
    difficulty: 5,
  });

  // Google Sheets URL - will be set from API response
  const [googleSheetsUrl, setGoogleSheetsUrl] = useState(null);

  // Reason for change - will be set from plan data
  const [reasonForChange, setReasonForChange] = useState(null);

  // Latest admin notes from checkin history
  const [latestAdminNotes, setLatestAdminNotes] = useState(null);

  useEffect(() => {
    loadWorkoutData();
  }, []);

  const loadWorkoutData = async () => {
    try {
      const [plan, todayData, checkinHistory] = await Promise.all([
        PlanService.getCurrentPlan(),
        WorkoutService.getTodayWorkout(),
        CheckInService.getCheckInHistory(1), // Get first page of checkin history
      ]);

      setCurrentPlan(plan);
      setTodayLog(todayData.todayLog);

      // Set Google Sheets URL from plan data
      if (plan?.googleSheetUrl) {
        setGoogleSheetsUrl(plan.googleSheetUrl);
      } else if (plan?.googleSheetId) {
        // Fallback: construct URL from ID if URL is not provided
        setGoogleSheetsUrl(`https://docs.google.com/spreadsheets/d/${plan.googleSheetId}`);
      }

      // Set reason for change from plan data
      if (plan?.reasonForChange) {
        setReasonForChange(plan.reasonForChange);
      }

      // Find latest admin notes from checkin history
      if (checkinHistory?.checkIns && checkinHistory.checkIns.length > 0) {
        const latestCheckinWithAdminNotes = checkinHistory.checkIns.find(checkin => checkin.adminNotes);
        if (latestCheckinWithAdminNotes) {
          setLatestAdminNotes(latestCheckinWithAdminNotes.adminNotes);
        }
      }

      // Get today's workout from the assigned plan, not from logs
      if (plan?.workoutPlan) {
        const today = getDayName();
        const todayWorkoutFromPlan = plan.workoutPlan.find(workout => workout.day === today);
        setTodayWorkout(todayWorkoutFromPlan);

        // Initialize workout data with today's assigned plan
        if (todayWorkoutFromPlan) {
          const exercises = todayWorkoutFromPlan.exercises.map(exercise => ({
            name: exercise.name,
            sets: Array(exercise.sets).fill(0).map(() => ({ reps: '', weight: '' })),
          }));
          setWorkoutData(prev => ({ ...prev, exercises }));
        }
      }
    } catch (error) {
      console.error('Workout data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDayName = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const openGoogleSheets = async () => {
    try {
      if (!googleSheetsUrl) {
        Alert.alert('No Google Sheet Available', 'Your personal Google Sheet will be created when your plan is generated or updated.');
        return;
      }

      const supported = await Linking.canOpenURL(googleSheetsUrl);
      if (supported) {
        await Linking.openURL(googleSheetsUrl);
      } else {
        Alert.alert('Error', 'Cannot open Google Sheets. Please check the URL.');
      }
    } catch (error) {
      console.error('Error opening Google Sheets:', error);
      Alert.alert('Error', 'Failed to open Google Sheets');
    }
  };

  const requestPlanChange = () => {
    Alert.alert(
      'Request Plan Change',
      'Would you like to request changes to your workout or diet plan? This will send an email to our admin team.',
      [
        {
          text: 'Cancel',
          style: 'cancel'
        },
        {
          text: 'Send Email',
          onPress: () => {
            const subject = 'Plan Change Request';
            const body = `Hi Admin Team,

I would like to request changes to my current plan. Please review my progress and suggest modifications.

Current Plan Version: ${currentPlan?.versionNumber || 'Unknown'}
Plan Type: ${currentPlan?.generatedBy || 'Unknown'}

Please let me know what changes you recommend.

Thank you!`;

            const emailUrl = `mailto:sweatandsnack2024@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            Linking.openURL(emailUrl).catch(() => {
              Alert.alert('Error', 'Could not open email client. Please email sweatandsnack2024@gmail.com directly.');
            });
          }
        }
      ]
    );
  };


  const getWorkoutForDay = (day) => {
    if (!currentPlan?.workoutPlan) return null;
    return currentPlan.workoutPlan.find(workout => workout.day === day);
  };

  const getDayColor = (day) => {
    const today = getDayName();
    if (day === today) return '#3B82F6';
    return '#666';
  };

  const getDayBackgroundColor = (day) => {
    const today = getDayName();
    if (day === today) return '#2C365E'; // Darker active day
    return '#e1e5e9'; // Dark inactive day
  };

  const handleSetChange = (exerciseIndex, setIndex, field, value) => {
    const newExercises = [...workoutData.exercises];
    newExercises[exerciseIndex].sets[setIndex][field] = value;
    setWorkoutData({ ...workoutData, exercises: newExercises });
  };

  const handleSubmitWorkout = async () => {
    try {
      const workoutLog = {
        date: new Date().toISOString(),
        day: getDayName(),
        exercises: workoutData.exercises.map(exercise => ({
          name: exercise.name,
          sets: exercise.sets.map(set => ({
            reps: parseInt(set.reps) || 0,
            weight: parseFloat(set.weight) || 0,
          })),
        })),
        totalWorkoutTime: parseInt(workoutData.totalWorkoutTime) || 0,
        notes: workoutData.notes,
        difficulty: workoutData.difficulty,
      };

      await WorkoutService.logWorkout(workoutLog);
      Alert.alert('Success', 'Workout logged successfully!');
      setShowLogModal(false);
      loadWorkoutData();
    } catch (error) {
      console.error('Submit workout error:', error);
      Alert.alert('Error', 'Failed to log workout. Please try again.');
    }
  };

  const renderExercise = (exercise, exerciseIndex) => (
    <View key={exerciseIndex} style={styles.exerciseCard}>
      <View style={styles.exerciseHeader}>
        <Text style={styles.exerciseName}>{exercise.name}</Text>
        <View style={styles.exerciseBadge}>
          <Text style={styles.exerciseBadgeText}>{exercise.sets} sets</Text>
        </View>
      </View>

      <View style={styles.exerciseDetails}>
        <View style={styles.exerciseDetailRow}>
          <Ionicons name="repeat" size={16} color="#666" />
          <Text style={styles.exerciseDetailText}>{exercise.reps} reps</Text>
        </View>

        {exercise.weight && exercise.weight !== 'Bodyweight' && (
          <View style={styles.exerciseDetailRow}>
            <Ionicons name="barbell" size={16} color="#666" />
            <Text style={styles.exerciseDetailText}>{exercise.weight}</Text>
          </View>
        )}

        {exercise.restTime && (
          <View style={styles.exerciseDetailRow}>
            <Ionicons name="time" size={16} color="#666" />
            <Text style={styles.exerciseDetailText}>Rest: {exercise.restTime}</Text>
          </View>
        )}
      </View>

      {exercise.notes && (
        <View style={styles.exerciseNotesContainer}>
          <Ionicons name="information-circle" size={16} color="#999" />
          <Text style={styles.exerciseNotes}>{exercise.notes}</Text>
        </View>
      )}
    </View>
  );

  const renderLogSet = (exercise, exerciseIndex) => (
    <View key={exerciseIndex} style={styles.logExerciseCard}>
      <Text style={styles.logExerciseName}>{exercise.name}</Text>
      {exercise.sets.map((set, setIndex) => (
        <View key={setIndex} style={styles.setRow}>
          <Text style={styles.setNumber}>Set {setIndex + 1}</Text>
          <TextInput
            style={styles.setInput}
            placeholderTextColor="#999"
            placeholder="Reps"
            value={set.reps}
            onChangeText={(value) => handleSetChange(exerciseIndex, setIndex, 'reps', value)}
            keyboardType="numeric"
          />
          <TextInput
            style={styles.setInput}
            placeholderTextColor="#999"
            placeholder="Weight"
            value={set.weight}
            onChangeText={(value) => handleSetChange(exerciseIndex, setIndex, 'weight', value)}
            keyboardType="numeric"
          />
        </View>
      ))}
    </View>
  );

  const renderWeeklyPlan = () => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return (
      <View style={styles.weeklyPlanContainer}>
        <View style={styles.weeklyPlanHeader}>
          <Text style={styles.weeklyPlanTitle}>Weekly Workout Plan</Text>
          <TouchableOpacity
            style={styles.toggleButton}
            onPress={() => setShowFullPlan(!showFullPlan)}
          >
            <Text style={styles.toggleButtonText}>
              {showFullPlan ? 'Hide' : 'Show'} Full Plan
            </Text>
            <Ionicons
              name={showFullPlan ? "chevron-up" : "chevron-down"}
              size={20}
              color="#3B82F6"
            />
          </TouchableOpacity>
        </View>

        {showFullPlan && (
          <View style={styles.daysContainer}>
            {days.map((day) => {
              const workout = getWorkoutForDay(day);
              const isToday = day === getDayName();

              return (
                <View key={day} style={styles.dayCard}>
                  <TouchableOpacity
                    style={[
                      styles.dayHeader,
                      { backgroundColor: getDayBackgroundColor(day) }
                    ]}
                    onPress={() => setSelectedDay(selectedDay === day ? null : day)}
                  >
                    <View style={styles.dayTitleContainer}>
                      <Text style={[styles.dayName, { color: getDayColor(day) }]}>
                        {day}
                        {isToday && <Text style={styles.todayIndicator}> (Today)</Text>}
                      </Text>
                      {workout?.session && (
                        <Text style={[styles.sessionName, { color: getDayColor(day) }]}>
                          {workout.session}
                        </Text>
                      )}
                    </View>
                    <View style={styles.dayInfo}>
                      {workout ? (
                        <Text style={styles.exerciseCount}>
                          {workout.exercises.length} exercises
                        </Text>
                      ) : (
                        <Text style={styles.restDay}>Rest Day</Text>
                      )}
                      <Ionicons
                        name={selectedDay === day ? "chevron-up" : "chevron-down"}
                        size={16}
                        color={getDayColor(day)}
                      />
                    </View>
                  </TouchableOpacity>

                  {selectedDay === day && workout && (
                    <View style={styles.dayExercises}>
                      {workout.exercises.map((exercise, index) => renderExercise(exercise, index))}
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <Text style={{ color: '#FFFFFF' }}>Loading workout plan...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <View style={[styles.safeArea, { paddingTop: insets.top }]}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={[styles.header, { paddingTop: 0 }]}>
          <Text style={styles.title}>Workout Plan</Text>
          <Text style={styles.subtitle}>{getDayName()}'s Workout</Text>
        </View>

        {/* Weekly Plan */}
        {currentPlan && renderWeeklyPlan()}

        {/* Today's Workout */}
        {todayWorkout ? (
          <View style={styles.workoutContainer}>
            <View style={styles.workoutHeader}>
              <Text style={styles.workoutTitle}>Today's Workout</Text>
              {todayWorkout.session && (
                <Text style={styles.workoutSession}>{todayWorkout.session}</Text>
              )}
              {/* <TouchableOpacity
              style={styles.logButton}
              onPress={() => setShowLogModal(true)}
            >
              <Ionicons name="add-circle" size={20} color="white" />
              <Text style={styles.logButtonText}>Log Workout</Text>
            </TouchableOpacity> */}
            </View>

            {todayWorkout.exercises.map((exercise, index) => renderExercise(exercise, index))}
          </View>
        ) : (
          <View style={styles.noWorkoutContainer}>
            <Ionicons name="barbell-outline" size={48} color="#ccc" />
            <Text style={styles.noWorkoutText}>No workout planned for today</Text>
            <Text style={styles.noWorkoutSubtext}>Rest day or check your plan</Text>
          </View>
        )}

        {/* Today's Log */}
        {todayLog && (
          <View style={styles.logContainer}>
            <Text style={styles.logTitle}>Today's Log</Text>
            <View style={styles.logStats}>
              <View style={styles.logStat}>
                <Text style={styles.logStatValue}>{todayLog.totalWorkoutTime} min</Text>
                <Text style={styles.logStatLabel}>Duration</Text>
              </View>
              <View style={styles.logStat}>
                <Text style={styles.logStatValue}>{todayLog.difficulty}/10</Text>
                <Text style={styles.logStatLabel}>Difficulty</Text>
              </View>
            </View>
            {todayLog.notes && (
              <Text style={styles.logNotes}>Notes: {todayLog.notes}</Text>
            )}
          </View>
        )}

        {/* Google Sheets Tracking */}
        <View style={styles.sheetsContainer}>
          <View style={styles.sheetsHeader}>
            <Ionicons name="document-text" size={24} color="#10B981" />
            <Text style={styles.sheetsTitle}>Track Your Progress</Text>
          </View>
          <Text style={styles.sheetsDescription}>
            {googleSheetsUrl
              ? "Access your personal Google Sheet to track your workouts, progress, and measurements over time."
              : "Your personal Google Sheet will be created when your plan is generated or updated."
            }
          </Text>
          <TouchableOpacity
            style={[styles.sheetsButton, !googleSheetsUrl && styles.sheetsButtonDisabled]}
            onPress={openGoogleSheets}
            disabled={!googleSheetsUrl}
          >
            <Ionicons name="open-outline" size={20} color="white" />
            <Text style={styles.sheetsButtonText}>
              {googleSheetsUrl ? 'Open My Google Sheet' : 'Google Sheet Not Available'}
            </Text>
          </TouchableOpacity>
          {googleSheetsUrl && (
            <Text style={styles.sheetsNote}>
              This is your personal sheet - only you have access to it
            </Text>
          )}
        </View>

        {/* Plan Change Request */}
        <View style={styles.planChangeContainer}>
          <View style={styles.planChangeHeader}>
            <Ionicons name="mail-outline" size={24} color="#FACC15" />
            <Text style={styles.planChangeTitle}>Need Plan Changes?</Text>
          </View>
          <Text style={styles.planChangeDescription}>
            If you need modifications to your workout or diet plan, email us at{' '}
            <Text style={styles.planChangeEmail} onPress={requestPlanChange}>
              sweatandsnack2024@gmail.com
            </Text>
            {' '}and we'll help you out!
          </Text>
        </View>

        {/* Reason for Change - Only show if admin notes exist */}
        {latestAdminNotes && (
          <View style={styles.reasonContainer}>
            <View style={styles.reasonHeader}>
              <Ionicons name="information-circle" size={20} color="#3B82F6" />
              <Text style={styles.reasonTitle}>Why your plan was updated</Text>
            </View>
            <Text style={styles.reasonText}>{latestAdminNotes}</Text>
            {currentPlan && (
              <Text style={styles.reasonDate}>
                Version {currentPlan.versionNumber} {new Date(currentPlan.createdAt).toLocaleDateString('en-GB')}
              </Text>
            )}
          </View>
        )}

        {/* Initial Plan Message for Human Plan */}
        {currentPlan?.generatedBy === 'Human' && currentPlan?.versionNumber === 1 && (
          <View style={[styles.reasonContainer, { borderLeftColor: '#FACC15' }]}>
            <View style={styles.reasonHeader}>
              <Ionicons name="information-circle" size={20} color="#FACC15" />
              <Text style={[styles.reasonTitle, { color: '#FACC15' }]}>Initial Plan</Text>
            </View>
            <Text style={styles.reasonText}>
              This is your starting plan. Your personal trainer may make adjustments to this plan.
            </Text>
          </View>
        )}

        {/* Plan Info */}
        {currentPlan && (
          <View style={styles.planInfoContainer}>
            <Text style={styles.planInfoTitle}>Plan Information</Text>
            <View style={styles.planInfoRow}>
              <Text style={styles.planInfoLabel}>Version:</Text>
              <Text style={styles.planInfoValue}>{currentPlan.versionNumber}</Text>
            </View>
            <View style={styles.planInfoRow}>
              <Text style={styles.planInfoLabel}>Generated by:</Text>
              <Text style={styles.planInfoValue}>{currentPlan.generatedBy}</Text>
            </View>
            <View style={styles.planInfoRow}>
              <Text style={styles.planInfoLabel}>Created:</Text>
              <Text style={styles.planInfoValue}>
                {new Date(currentPlan.createdAt).toLocaleDateString('en-GB')}
              </Text>
            </View>
          </View>
        )}

        {/* Log Workout Modal */}
        <Modal
          visible={showLogModal}
          animationType="slide"
          presentationStyle="pageSheet"
        >
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <TouchableOpacity onPress={() => setShowLogModal(false)}>
                <Text style={styles.cancelButton}>Cancel</Text>
              </TouchableOpacity>
              <Text style={styles.modalTitle}>Log Workout</Text>
              <TouchableOpacity onPress={handleSubmitWorkout}>
                <Text style={styles.saveButton}>Save</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalContent}>
              {workoutData.exercises.map((exercise, index) => renderLogSet(exercise, index))}

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Workout Duration (minutes)</Text>
                <TextInput
                  style={styles.input}
                  placeholderTextColor="#999"
                  value={workoutData.totalWorkoutTime}
                  onChangeText={(value) => setWorkoutData({ ...workoutData, totalWorkoutTime: value })}
                  keyboardType="numeric"
                  placeholder="Enter duration"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Difficulty (1-10)</Text>
                <TextInput
                  style={styles.input}
                  placeholderTextColor="#999"
                  value={workoutData.difficulty.toString()}
                  onChangeText={(value) => setWorkoutData({ ...workoutData, difficulty: parseInt(value) || 5 })}
                  keyboardType="numeric"
                  placeholder="Rate difficulty"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Notes</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  placeholderTextColor="#999"
                  value={workoutData.notes}
                  onChangeText={(value) => setWorkoutData({ ...workoutData, notes: value })}
                  placeholder="Add any notes about your workout"
                  multiline
                  numberOfLines={3}
                />
              </View>
            </ScrollView>
          </View>
        </Modal>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  container: {
    flex: 1,
    backgroundColor: '#0B0E14',
    paddingBottom: 0,
  },
  scrollContent: {
    paddingBottom: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: SCREEN_HEIGHT * 0.7,
  },
  header: {
    backgroundColor: '#161C2D',
    paddingHorizontal: Math.max(16, SCREEN_WIDTH * 0.05),
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#2C365E',
    shadowcolor: '#FFFFFF',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: 16,
    color: '#6D759E',
    marginTop: 4,
  },
  logoutButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#0B0E14',
  },
  workoutContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  workoutHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  workoutTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  workoutSession: {
    fontSize: 14,
    fontWeight: '400',
    color: '#6D759E',
    marginTop: 4,
  },
  logButton: {
    backgroundColor: '#3B82F6',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
  },
  logButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  exerciseCard: {
    backgroundColor: '#0B0E14',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  exerciseHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  exerciseName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    flex: 1,
  },
  exerciseBadge: {
    backgroundColor: '#3B82F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  exerciseBadgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  exerciseDetails: {
    marginBottom: 8,
  },
  exerciseDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  exerciseDetailText: {
    fontSize: 14,
    color: '#6D759E',
    marginLeft: 8,
  },
  exerciseNotesContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#2C365E',
  },
  exerciseNotes: {
    fontSize: 12,
    color: '#6D759E',
    fontStyle: 'italic',
    marginLeft: 8,
    flex: 1,
  },
  noWorkoutContainer: {
    alignItems: 'center',
    padding: 40,
    backgroundColor: '#161C2D',
    margin: 20,
    borderRadius: 16,
  },
  noWorkoutText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#6D759E',
    marginTop: 16,
  },
  noWorkoutSubtext: {
    fontSize: 14,
    color: '#6D759E',
    marginTop: 4,
  },
  logContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  logTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 12,
  },
  logStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  logStat: {
    alignItems: 'center',
  },
  logStatValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#3B82F6',
  },
  logStatLabel: {
    fontSize: 12,
    color: '#6D759E',
    marginTop: 4,
  },
  logNotes: {
    fontSize: 14,
    color: '#6D759E',
    marginTop: 12,
    fontStyle: 'italic',
  },
  planInfoContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  planInfoTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 12,
  },
  planInfoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  planInfoLabel: {
    fontSize: 14,
    color: '#6D759E',
  },
  planInfoValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    paddingTop: 60,
    backgroundColor: '#161C2D',
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  cancelButton: {
    fontSize: 16,
    color: '#3B82F6',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  saveButton: {
    fontSize: 16,
    color: '#3B82F6',
    fontWeight: '600',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  logExerciseCard: {
    backgroundColor: '#161C2D',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  logExerciseName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: 12,
  },
  setRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  setNumber: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6D759E',
    width: 60,
  },
  setInput: {
    flex: 1,
    backgroundColor: '#0B0E14',
    color: '#FFFFFF',
    padding: 12,
    borderRadius: 8,
    marginHorizontal: 8,
    fontSize: 16,
  },
  inputGroup: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#161C2D',
    color: '#FFFFFF',
    padding: 16,
    borderRadius: 12,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  // Weekly Plan Styles
  weeklyPlanContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  weeklyPlanHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  weeklyPlanTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  toggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#0B0E14',
    borderRadius: 20,
  },
  toggleButtonText: {
    fontSize: 14,
    color: '#3B82F6',
    fontWeight: '500',
    marginRight: 4,
  },
  daysContainer: {
    marginTop: 8,
  },
  dayCard: {
    marginBottom: 12,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e1e5e9',
  },
  dayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  dayName: {
    fontSize: 16,
    fontWeight: '600',
  },
  dayTitleContainer: {
    flex: 1,
  },
  sessionName: {
    fontSize: 12,
    fontWeight: '400',
    marginTop: 2,
    opacity: 0.8,
  },
  todayIndicator: {
    fontSize: 12,
    color: '#3B82F6',
    fontWeight: '400',
  },
  dayInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  exerciseCount: {
    fontSize: 14,
    color: '#6D759E',
    marginRight: 8,
  },
  restDay: {
    fontSize: 14,
    color: '#6D759E',
    fontStyle: 'italic',
    marginRight: 8,
  },
  dayExercises: {
    padding: 16,
    backgroundColor: '#0B0E14',
    borderTopWidth: 1,
    borderTopColor: '#2C365E',
  },
  // Google Sheets Styles
  sheetsContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 20,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sheetsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sheetsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 12,
  },
  sheetsDescription: {
    fontSize: 14,
    color: '#6D759E',
    lineHeight: 20,
    marginBottom: 16,
  },
  sheetsButton: {
    backgroundColor: '#10B981',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    marginBottom: 12,
  },
  sheetsButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  sheetsButtonDisabled: {
    backgroundColor: '#ccc',
    opacity: 0.6,
  },
  sheetsNote: {
    fontSize: 12,
    color: '#6D759E',
    fontStyle: 'italic',
    textAlign: 'center',
  },
  planChangeContainer: {
    backgroundColor: '#161C2D',
    margin: 20,
    marginTop: 0,
    borderRadius: 16,
    padding: 16,
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  planChangeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  planChangeTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  planChangeDescription: {
    fontSize: 14,
    color: '#6D759E',
    lineHeight: 20,
  },
  planChangeEmail: {
    color: '#FACC15',
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  // Reason for Change Styles
  reasonContainer: {
    backgroundColor: '#161C2D',
    borderRadius: 12,
    padding: 16,
    margin: 20,
    marginTop: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#3B82F6',
  },
  reasonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  reasonTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#3B82F6',
    marginLeft: 8,
  },
  reasonText: {
    fontSize: 14,
    color: '#6D759E',
    lineHeight: 20,
    marginBottom: 8,
  },
  reasonDate: {
    fontSize: 12,
    color: '#6D759E',
    fontStyle: 'italic',
  },
});

export default WorkoutPlanScreen;
